export type FollowEntityType = 'radio' | 'programa' | 'locutor' | 'club' | 'evento' | 'concurso';
export type FollowNotificationType = 'program_start' | 'host_start' | 'radio_live' | 'new_contest' | 'new_event';

export interface FollowableEntity {
  id: string;
  type: FollowEntityType;
  name: string;
  subtitle: string;
  imageUrl: string;
  followers: number;
  weeklyGrowth: number;
  monthlyGrowth: number;
  trend: 'exploding' | 'up' | 'stable' | 'down';
  trendPercent: number;
  scoreReward: number;
  tags: string[];
  linkUrl: string;
  isLive?: boolean;
  liveLabel?: string;
  entityParent?: string;
  entityParentUrl?: string;
}

export interface FollowNotification {
  type: FollowNotificationType;
  label: string;
  description: string;
  icon: string;
  color: string;
  bgColor: string;
  enabled: boolean;
}

export interface ListenerFollowedProfile {
  listenerName: string;
  listenerAvatar: string;
  listenerLevel: number;
  listenerTier: string;
  followedRadios: string[];
  followedProgramas: string[];
  followedLocutores: string[];
  followedClubes: string[];
  totalFollowing: number;
  followerScore: number;
}

export interface FollowScoreAction {
  action: string;
  points: number;
  icon: string;
  color: string;
  description: string;
}

export const followEntityTypeLabels: Record<FollowEntityType, string> = {
  radio: 'Radio',
  programa: 'Programa',
  locutor: 'Locutor',
  club: 'Club',
  evento: 'Evento',
  concurso: 'Concurso',
};

export const followEntityTypeIcons: Record<FollowEntityType, string> = {
  radio: 'ri-radio-line',
  programa: 'ri-tv-line',
  locutor: 'ri-user-voice-line',
  club: 'ri-shield-star-line',
  evento: 'ri-calendar-event-line',
  concurso: 'ri-trophy-line',
};

export const followEntityTypeColors: Record<FollowEntityType, string> = {
  radio: 'bg-pink-50 text-pink-700 border-pink-200',
  programa: 'bg-teal-50 text-teal-700 border-teal-200',
  locutor: 'bg-amber-50 text-amber-700 border-amber-200',
  club: 'bg-violet-50 text-violet-700 border-violet-200',
  evento: 'bg-cyan-50 text-cyan-700 border-cyan-200',
  concurso: 'bg-orange-50 text-orange-700 border-orange-200',
};

export const followEntityTypeBgLight: Record<FollowEntityType, string> = {
  radio: 'bg-pink-50',
  programa: 'bg-teal-50',
  locutor: 'bg-amber-50',
  club: 'bg-violet-50',
  evento: 'bg-cyan-50',
  concurso: 'bg-orange-50',
};

export const followEntityTypeTextColor: Record<FollowEntityType, string> = {
  radio: 'text-pink-700',
  programa: 'text-teal-700',
  locutor: 'text-amber-700',
  club: 'text-violet-700',
  evento: 'text-cyan-700',
  concurso: 'text-orange-700',
};

export const trendConfig: Record<string, { label: string; icon: string; color: string; bgColor: string }> = {
  exploding: { label: 'Explosivo', icon: 'ri-rocket-line', color: 'text-red-600', bgColor: 'bg-red-50' },
  up: { label: 'En Ascenso', icon: 'ri-arrow-up-line', color: 'text-emerald-600', bgColor: 'bg-emerald-50' },
  stable: { label: 'Estable', icon: 'ri-subtract-line', color: 'text-foreground-500', bgColor: 'bg-foreground-100' },
  down: { label: 'Bajando', icon: 'ri-arrow-down-line', color: 'text-red-500', bgColor: 'bg-red-50' },
};

export const followNotifications: FollowNotification[] = [
  {
    type: 'radio_live',
    label: 'Radio en Vivo',
    description: 'Te avisa cuando una radio que seguís inicia transmisión en vivo.',
    icon: 'ri-broadcast-line',
    color: 'text-pink-600',
    bgColor: 'bg-pink-50',
    enabled: true,
  },
  {
    type: 'program_start',
    label: 'Inicio de Programa',
    description: 'Te avisa cuando un programa que seguís está por comenzar.',
    icon: 'ri-play-circle-line',
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    enabled: true,
  },
  {
    type: 'host_start',
    label: 'Inicio de Locutor',
    description: 'Te avisa cuando un locutor que seguís entra al aire.',
    icon: 'ri-user-voice-line',
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    enabled: true,
  },
  {
    type: 'new_contest',
    label: 'Nuevo Concurso',
    description: 'Te avisa cuando se publica un nuevo concurso de entidades que seguís.',
    icon: 'ri-trophy-line',
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    enabled: true,
  },
  {
    type: 'new_event',
    label: 'Nuevo Evento',
    description: 'Te avisa cuando se publica un nuevo evento de entidades que seguís.',
    icon: 'ri-calendar-event-line',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-50',
    enabled: false,
  },
];

export const followScoreActions: FollowScoreAction[] = [
  {
    action: 'Nuevo Seguidor',
    points: 10,
    icon: 'ri-user-add-line',
    color: 'text-emerald-600',
    description: 'Sumás 10 puntos por cada nuevo seguidor que gana tu entidad.',
  },
  {
    action: 'Retención de Seguidores',
    points: 5,
    icon: 'ri-user-heart-line',
    color: 'text-sky-600',
    description: 'Sumás 5 puntos por cada seguidor que permanece activo durante 30 días.',
  },
  {
    action: 'Crecimiento de Seguidores',
    points: 100,
    icon: 'ri-line-chart-line',
    color: 'text-violet-600',
    description: 'Sumás 100 puntos cada vez que alcanzás un hito de crecimiento (+100, +500, +1K, +5K, +10K seguidores).',
  },
];

export const followerDashboardStats = {
  totalFollows: 1247000,
  weeklyFollows: 42800,
  monthlyFollows: 186500,
  avgFollowsPerListener: 12.4,
  mostFollowedType: 'radio' as FollowEntityType,
  mostFollowedCount: 487000,
  trendingNow: 127,
  retentionRate: 78.3,
  activeListenersFollowing: 89200,
  notificationsSent: 342000,
};

export const listenerProfile: ListenerFollowedProfile = {
  listenerName: 'Marco Rivera',
  listenerAvatar: 'https://readdy.ai/api/search-image?query=Young%20Latino%20man%20street%20style%20portrait%2C%20casual%20genuine%20smile%2C%20warm%20urban%20background%2C%20lifestyle%20editorial%20photography%2C%20natural%20light%2C%20approachable%20look&width=120&height=120&seq=follow-marco-rivera&orientation=squarish',
  listenerLevel: 4,
  listenerTier: 'Top Fan',
  followedRadios: ['radio-001', 'radio-003', 'radio-005', 'radio-002'],
  followedProgramas: ['programa-001', 'programa-003', 'programa-005'],
  followedLocutores: ['locutor-001', 'locutor-003', 'locutor-005', 'locutor-002', 'locutor-006'],
  followedClubes: ['club-001', 'club-003', 'club-005'],
  totalFollowing: 15,
  followerScore: 1240,
};

// ─── MOCK ENTITIES ────────────────────────────────────────

export const followableEntities: FollowableEntity[] = [
  // ═══ RADIOS (6) ═══
  {
    id: 'radio-001',
    type: 'radio',
    name: 'Banda Radio MX',
    subtitle: '103.50 FM · Regional Mexicano',
    imageUrl: 'https://readdy.ai/api/search-image?query=Banda%20music%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20brass%20instruments%20and%20Mexican%20motifs%2C%20bold%20red%20white%20and%20green%20colors%2C%20festive%20regional%20brand%20identity&width=160&height=160&seq=follow-banda-mx&orientation=squarish',
    followers: 48700,
    weeklyGrowth: 1240,
    monthlyGrowth: 5200,
    trend: 'exploding',
    trendPercent: 11.2,
    scoreReward: 150,
    tags: ['Regional Mexicano', 'Banda', 'Norteño'],
    linkUrl: '/radio',
    isLive: true,
    liveLabel: 'AL AIRE',
    entityParent: 'México',
    entityParentUrl: '/radios',
  },
  {
    id: 'radio-002',
    type: 'radio',
    name: 'Radio Nueva Actitud',
    subtitle: '94.70 FM · Pop y Éxitos',
    imageUrl: 'https://readdy.ai/api/search-image?query=Modern%20radio%20station%20icon%20logo%20design%2C%20circular%20bright%20pop%20music%20theme%2C%20clean%20minimal%20style%2C%20coral%20and%20warm%20tones%2C%20professional%20broadcast%20identity&width=160&height=160&seq=follow-nueva-actitud&orientation=squarish',
    followers: 42300,
    weeklyGrowth: 980,
    monthlyGrowth: 4100,
    trend: 'up',
    trendPercent: 8.7,
    scoreReward: 140,
    tags: ['Pop', 'Éxitos', 'Entretenimiento'],
    linkUrl: '/radio',
    isLive: true,
    liveLabel: 'AL AIRE',
    entityParent: 'Argentina',
    entityParentUrl: '/radios',
  },
  {
    id: 'radio-003',
    type: 'radio',
    name: 'La Mexicana FM',
    subtitle: '107.90 FM · Regional Mexicano',
    imageUrl: 'https://readdy.ai/api/search-image?query=Mexican%20regional%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20vibrant%20green%20red%20and%20white%20colors%2C%20traditional%20Mexican%20aesthetic%2C%20bold%20festive%20brand%20identity&width=160&height=160&seq=follow-mexicana-fm&orientation=squarish',
    followers: 38900,
    weeklyGrowth: 760,
    monthlyGrowth: 3400,
    trend: 'up',
    trendPercent: 6.8,
    scoreReward: 130,
    tags: ['Regional', 'Mexicano', 'Cultura'],
    linkUrl: '/radio',
    isLive: false,
    entityParent: 'México',
    entityParentUrl: '/radios',
  },
  {
    id: 'radio-004',
    type: 'radio',
    name: 'Rock Total Radio',
    subtitle: '87.01 FM · Rock y Metal',
    imageUrl: 'https://readdy.ai/api/search-image?query=Rock%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20electric%20guitar%20and%20skull%20motifs%2C%20dark%20edgy%20aesthetic%2C%20bold%20metallic%20tones&width=160&height=160&seq=follow-rock-total&orientation=squarish',
    followers: 31200,
    weeklyGrowth: 540,
    monthlyGrowth: 2400,
    trend: 'stable',
    trendPercent: 5.1,
    scoreReward: 120,
    tags: ['Rock', 'Metal', 'Alternativo'],
    linkUrl: '/radio',
    isLive: false,
    entityParent: 'Argentina',
    entityParentUrl: '/radios',
  },
  {
    id: 'radio-005',
    type: 'radio',
    name: 'Deportes Total Radio',
    subtitle: '200.00 FM · Deportes',
    imageUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20football%20and%20star%20motifs%2C%20bold%20energetic%20colors%2C%20dynamic%20sports%20brand%20identity&width=160&height=160&seq=follow-deportes-total&orientation=squarish',
    followers: 28400,
    weeklyGrowth: 890,
    monthlyGrowth: 3800,
    trend: 'up',
    trendPercent: 9.4,
    scoreReward: 120,
    tags: ['Deportes', 'Fútbol', 'Análisis'],
    linkUrl: '/radio',
    isLive: true,
    liveLabel: 'AL AIRE',
    entityParent: 'Estados Unidos',
    entityParentUrl: '/radios',
  },
  {
    id: 'radio-006',
    type: 'radio',
    name: 'La Kalle 99.9',
    subtitle: '99.90 FM · Urbano y Reggaetón',
    imageUrl: 'https://readdy.ai/api/search-image?query=Urban%20pop%20radio%20station%20logo%20design%2C%20circular%20emblem%20with%20energetic%20graffiti%20style%2C%20vibrant%20neon%20colors%2C%20street%20culture%20brand%20identity%2C%20bold%20youthful%20aesthetic&width=160&height=160&seq=follow-la-kalle&orientation=squarish',
    followers: 35600,
    weeklyGrowth: 1100,
    monthlyGrowth: 4900,
    trend: 'exploding',
    trendPercent: 12.4,
    scoreReward: 140,
    tags: ['Urbano', 'Reggaetón', 'Juvenil'],
    linkUrl: '/radio',
    isLive: false,
    entityParent: 'Colombia',
    entityParentUrl: '/radios',
  },

  // ═══ PROGRAMAS (6) ═══
  {
    id: 'programa-001',
    type: 'programa',
    name: 'Mañanas Argentinas',
    subtitle: 'Lun-Vie · 6:00-10:00 AM',
    imageUrl: 'https://readdy.ai/api/search-image?query=Morning%20show%20program%20logo%20design%2C%20circular%20warm%20sunrise%20colors%20with%20coffee%20cup%20motif%2C%20friendly%20inviting%20broadcast%20brand%20identity%2C%20clean%20modern%20aesthetic&width=160&height=160&seq=follow-mañanas-argentinas&orientation=squarish',
    followers: 22100,
    weeklyGrowth: 650,
    monthlyGrowth: 2800,
    trend: 'up',
    trendPercent: 8.2,
    scoreReward: 110,
    tags: ['Matutino', 'Noticias', 'Entretenimiento'],
    linkUrl: '/program/mañanas-argentinas',
    isLive: true,
    liveLabel: 'AL AIRE',
    entityParent: 'Radio Nueva Actitud',
    entityParentUrl: '/radio',
  },
  {
    id: 'programa-002',
    type: 'programa',
    name: 'Carrusel Deportivo',
    subtitle: 'Sáb-Dom · 14:00-18:00',
    imageUrl: 'https://readdy.ai/api/search-image?query=Sports%20radio%20program%20logo%20design%2C%20circular%20soccer%20ball%20dynamic%20motif%2C%20green%20field%20colors%2C%20energetic%20sports%20broadcast%20brand%20identity%2C%20bold%20typography&width=160&height=160&seq=follow-carrusel-deportivo&orientation=squarish',
    followers: 18900,
    weeklyGrowth: 480,
    monthlyGrowth: 2100,
    trend: 'stable',
    trendPercent: 5.4,
    scoreReward: 100,
    tags: ['Deportes', 'Fútbol', 'Fin de Semana'],
    linkUrl: '/program/carrusel-deportivo',
    isLive: false,
    entityParent: 'Deportes Total Radio',
    entityParentUrl: '/radio',
  },
  {
    id: 'programa-003',
    type: 'programa',
    name: 'Tardes con Vale',
    subtitle: 'Lun-Vie · 15:00-19:00',
    imageUrl: 'https://readdy.ai/api/search-image?query=Afternoon%20talk%20show%20program%20logo%20design%2C%20circular%20warm%20sunset%20gradient%2C%20feminine%20elegant%20typography%2C%20soft%20coral%20and%20gold%20colors%2C%20lifestyle%20broadcast%20identity&width=160&height=160&seq=follow-tardes-con-vale&orientation=squarish',
    followers: 16700,
    weeklyGrowth: 720,
    monthlyGrowth: 3100,
    trend: 'exploding',
    trendPercent: 10.8,
    scoreReward: 110,
    tags: ['Tarde', 'Talk Show', 'Música'],
    linkUrl: '/program/tardes-con-vale',
    isLive: true,
    liveLabel: 'AL AIRE',
    entityParent: 'Radio Nueva Actitud',
    entityParentUrl: '/radio',
  },
  {
    id: 'programa-004',
    type: 'programa',
    name: 'Deporte Total',
    subtitle: 'Lun-Vie · 20:00-22:00',
    imageUrl: 'https://readdy.ai/api/search-image?query=Evening%20sports%20show%20program%20logo%20design%2C%20circular%20stadium%20lights%20motif%2C%20navy%20and%20gold%20colors%2C%20premium%20sports%20broadcast%20brand%20identity%2C%20bold%20masculine%20typography&width=160&height=160&seq=follow-deporte-total&orientation=squarish',
    followers: 15400,
    weeklyGrowth: 390,
    monthlyGrowth: 1700,
    trend: 'up',
    trendPercent: 6.1,
    scoreReward: 100,
    tags: ['Deportes', 'Noche', 'Análisis'],
    linkUrl: '/program/deporte-total',
    isLive: false,
    entityParent: 'Deportes Total Radio',
    entityParentUrl: '/radio',
  },
  {
    id: 'programa-005',
    type: 'programa',
    name: 'Onda Nocturna',
    subtitle: 'Lun-Vie · 22:00-02:00',
    imageUrl: 'https://readdy.ai/api/search-image?query=Late%20night%20radio%20program%20logo%20design%2C%20circular%20moon%20and%20stars%20motif%2C%20deep%20purple%20and%20silver%20colors%2C%20mysterious%20atmospheric%20broadcast%20identity%2C%20elegant%20nocturnal%20aesthetic&width=160&height=160&seq=follow-onda-nocturna&orientation=squarish',
    followers: 13200,
    weeklyGrowth: 560,
    monthlyGrowth: 2400,
    trend: 'up',
    trendPercent: 7.9,
    scoreReward: 100,
    tags: ['Nocturno', 'Música', 'Relax'],
    linkUrl: '/program/onda-nocturna',
    isLive: true,
    liveLabel: 'AL AIRE',
    entityParent: 'Rock Total Radio',
    entityParentUrl: '/radio',
  },
  {
    id: 'programa-006',
    type: 'programa',
    name: 'Modo Creativo',
    subtitle: 'Miér · 18:00-20:00',
    imageUrl: 'https://readdy.ai/api/search-image?query=Creative%20arts%20program%20logo%20design%2C%20circular%20paint%20palette%20and%20brush%20motif%2C%20vibrant%20multicolor%20splashes%2C%20artistic%20modern%20broadcast%20identity%2C%20playful%20creative%20aesthetic&width=160&height=160&seq=follow-modo-creativo&orientation=squarish',
    followers: 9800,
    weeklyGrowth: 420,
    monthlyGrowth: 1800,
    trend: 'exploding',
    trendPercent: 14.2,
    scoreReward: 90,
    tags: ['Creatividad', 'Cultura', 'Arte'],
    linkUrl: '/program/modo-creativo',
    isLive: false,
    entityParent: 'La Kalle 99.9',
    entityParentUrl: '/radio',
  },

  // ═══ LOCUTORES (6) ═══
  {
    id: 'locutor-001',
    type: 'locutor',
    name: 'Valentina Ríos',
    subtitle: 'Locutora · Radio Nueva Actitud',
    imageUrl: 'https://readdy.ai/api/search-image?query=Professional%20Latina%20radio%20host%20portrait%2C%20confident%20warm%20smile%2C%20studio%20with%20microphones%2C%20golden%20hour%20lighting%2C%20clean%20editorial%20photography&width=160&height=160&seq=follow-valentina-rios&orientation=squarish',
    followers: 31200,
    weeklyGrowth: 870,
    monthlyGrowth: 3800,
    trend: 'exploding',
    trendPercent: 10.5,
    scoreReward: 150,
    tags: ['Locutora #1', 'Talk Show', 'Carismática'],
    linkUrl: '/host/valentina-rios',
    isLive: true,
    liveLabel: 'EN CABINA',
    entityParent: 'Radio Nueva Actitud',
    entityParentUrl: '/radio',
  },
  {
    id: 'locutor-002',
    type: 'locutor',
    name: 'Ricky Navarro',
    subtitle: 'Locutor · Banda Radio MX',
    imageUrl: 'https://readdy.ai/api/search-image?query=Charismatic%20Latino%20radio%20host%20portrait%2C%20warm%20smile%2C%20professional%20studio%20setting%2C%20golden%20lighting%2C%20clean%20background%2C%20editorial%20photography&width=160&height=160&seq=follow-ricky-navarro&orientation=squarish',
    followers: 28400,
    weeklyGrowth: 650,
    monthlyGrowth: 2900,
    trend: 'up',
    trendPercent: 7.8,
    scoreReward: 140,
    tags: ['Carismático', 'Regional', 'Banda'],
    linkUrl: '/host/ricky-navarro',
    isLive: true,
    liveLabel: 'EN CABINA',
    entityParent: 'Banda Radio MX',
    entityParentUrl: '/radio',
  },
  {
    id: 'locutor-003',
    type: 'locutor',
    name: 'Carlos Mendoza',
    subtitle: 'Locutor · Deportes Total Radio',
    imageUrl: 'https://readdy.ai/api/search-image?query=Latino%20sports%20commentator%20portrait%2C%20energetic%20expression%2C%20professional%20broadcast%20studio%2C%20dynamic%20lighting%2C%20clean%20editorial%20photography&width=160&height=160&seq=follow-carlos-mendoza&orientation=squarish',
    followers: 19800,
    weeklyGrowth: 520,
    monthlyGrowth: 2300,
    trend: 'up',
    trendPercent: 6.9,
    scoreReward: 120,
    tags: ['Deportes', 'Comentarista', 'Fútbol'],
    linkUrl: '/host/carlos-mendoza',
    isLive: false,
    entityParent: 'Deportes Total Radio',
    entityParentUrl: '/radio',
  },
  {
    id: 'locutor-004',
    type: 'locutor',
    name: 'Sofía Linares',
    subtitle: 'Locutora · La Kalle 99.9',
    imageUrl: 'https://readdy.ai/api/search-image?query=Young%20Latina%20radio%20DJ%20portrait%2C%20trendy%20street%20style%2C%20urban%20studio%20with%20neon%20lights%2C%20vibrant%20energetic%20atmosphere%2C%20editorial%20lifestyle%20photography&width=160&height=160&seq=follow-sofia-linares&orientation=squarish',
    followers: 22100,
    weeklyGrowth: 940,
    monthlyGrowth: 4100,
    trend: 'exploding',
    trendPercent: 13.1,
    scoreReward: 130,
    tags: ['Urbano', 'Juvenil', 'DJ'],
    linkUrl: '/host/sofia-linares',
    isLive: true,
    liveLabel: 'EN CABINA',
    entityParent: 'La Kalle 99.9',
    entityParentUrl: '/radio',
  },
  {
    id: 'locutor-005',
    type: 'locutor',
    name: 'Diego Montenegro',
    subtitle: 'Locutor y Comediante · Rock Total Radio',
    imageUrl: 'https://readdy.ai/api/search-image?query=Latino%20comedian%20radio%20host%20portrait%2C%20humorous%20expression%2C%20casual%20relaxed%20studio%20vibe%2C%20warm%20atmospheric%20lighting%2C%20authentic%20personality%20photography&width=160&height=160&seq=follow-diego-montenegro&orientation=squarish',
    followers: 16500,
    weeklyGrowth: 380,
    monthlyGrowth: 1700,
    trend: 'stable',
    trendPercent: 4.2,
    scoreReward: 110,
    tags: ['Comedia', 'Rock', 'Entretenimiento'],
    linkUrl: '/host/diego-montenegro',
    isLive: false,
    entityParent: 'Rock Total Radio',
    entityParentUrl: '/radio',
  },
  {
    id: 'locutor-006',
    type: 'locutor',
    name: 'Roberto Aguilar',
    subtitle: 'Locutor · La Mexicana FM',
    imageUrl: 'https://readdy.ai/api/search-image?query=Mature%20Latino%20radio%20host%20portrait%2C%20distinguished%20silver%20hair%2C%20warm%20professional%20studio%20setting%2C%20classic%20broadcast%20atmosphere%2C%20authoritative%20dignified%20photography&width=160&height=160&seq=follow-roberto-aguilar&orientation=squarish',
    followers: 14200,
    weeklyGrowth: 290,
    monthlyGrowth: 1300,
    trend: 'stable',
    trendPercent: 3.8,
    scoreReward: 100,
    tags: ['Regional', 'Experimentado', 'Cultura'],
    linkUrl: '/host/roberto-aguilar',
    isLive: false,
    entityParent: 'La Mexicana FM',
    entityParentUrl: '/radio',
  },

  // ═══ CLUBES (6) ═══
  {
    id: 'club-001',
    type: 'club',
    name: 'Banda MX Army',
    subtitle: 'Club Oficial · 12,400 miembros',
    imageUrl: 'https://readdy.ai/api/search-image?query=Exclusive%20fan%20club%20logo%20design%2C%20circular%20emblem%20with%20crown%20and%20shield%20motifs%2C%20royal%20gold%20and%20crimson%20colors%2C%20premium%20community%20brand%20identity%2C%20elegant%20luxurious%20aesthetic&width=160&height=160&seq=follow-banda-mx-army&orientation=squarish',
    followers: 12400,
    weeklyGrowth: 340,
    monthlyGrowth: 1500,
    trend: 'up',
    trendPercent: 7.2,
    scoreReward: 100,
    tags: ['Club Oficial', 'VIP', 'Banda'],
    linkUrl: '/clubes',
    isLive: false,
    entityParent: 'Banda Radio MX',
    entityParentUrl: '/radio',
  },
  {
    id: 'club-002',
    type: 'club',
    name: 'La Mexicana Fan Club',
    subtitle: 'Club Oficial · 9,800 miembros',
    imageUrl: 'https://readdy.ai/api/search-image?query=Mexican%20culture%20fan%20club%20logo%20design%2C%20circular%20emblem%20with%20traditional%20patterns%20and%20sombrero%20motifs%2C%20vibrant%20green%20red%20white%20colors%2C%20community%20brand%20identity%2C%20festive%20cultural%20aesthetic&width=160&height=160&seq=follow-mexicana-fan-club&orientation=squarish',
    followers: 9800,
    weeklyGrowth: 280,
    monthlyGrowth: 1200,
    trend: 'stable',
    trendPercent: 5.4,
    scoreReward: 90,
    tags: ['Club Oficial', 'Cultura', 'Eventos'],
    linkUrl: '/clubes',
    isLive: false,
    entityParent: 'La Mexicana FM',
    entityParentUrl: '/radio',
  },
  {
    id: 'club-003',
    type: 'club',
    name: 'Club VIP Nueva Actitud',
    subtitle: 'Club VIP · 5,600 miembros',
    imageUrl: 'https://readdy.ai/api/search-image?query=Premium%20VIP%20club%20logo%20design%2C%20circular%20diamond%20and%20sparkle%20emblem%2C%20elegant%20rose%20gold%20and%20champagne%20colors%2C%20exclusive%20luxury%20community%20identity%2C%20sophisticated%20refined%20aesthetic&width=160&height=160&seq=follow-club-vip-nueva-actitud&orientation=squarish',
    followers: 5600,
    weeklyGrowth: 210,
    monthlyGrowth: 900,
    trend: 'up',
    trendPercent: 6.8,
    scoreReward: 80,
    tags: ['VIP', 'Premium', 'Exclusivo'],
    linkUrl: '/clubes',
    isLive: false,
    entityParent: 'Radio Nueva Actitud',
    entityParentUrl: '/radio',
  },
  {
    id: 'club-004',
    type: 'club',
    name: 'Rockeros Unidos',
    subtitle: 'Club Oficial · 7,200 miembros',
    imageUrl: 'https://readdy.ai/api/search-image?query=Rock%20music%20fan%20club%20logo%20design%2C%20circular%20emblem%20with%20guitar%20picks%20and%20lightning%20bolts%2C%20edgy%20black%20and%20silver%20aesthetic%2C%20rebellious%20community%20brand%20identity%2C%20bold%20rock%20culture%20design&width=160&height=160&seq=follow-rockeros-unidos&orientation=squarish',
    followers: 7200,
    weeklyGrowth: 180,
    monthlyGrowth: 800,
    trend: 'stable',
    trendPercent: 4.1,
    scoreReward: 80,
    tags: ['Club Oficial', 'Rock', 'Conciertos'],
    linkUrl: '/clubes',
    isLive: false,
    entityParent: 'Rock Total Radio',
    entityParentUrl: '/radio',
  },
  {
    id: 'club-005',
    type: 'club',
    name: 'La Kalle Crew',
    subtitle: 'Club Oficial · 8,900 miembros',
    imageUrl: 'https://readdy.ai/api/search-image?query=Urban%20street%20culture%20fan%20club%20logo%20design%2C%20circular%20graffiti%20and%20spray%20paint%20emblem%2C%20vibrant%20neon%20colors%2C%20hip%20hop%20community%20brand%20identity%2C%20bold%20youthful%20rebellious%20aesthetic&width=160&height=160&seq=follow-la-kalle-crew&orientation=squarish',
    followers: 8900,
    weeklyGrowth: 420,
    monthlyGrowth: 1800,
    trend: 'exploding',
    trendPercent: 11.8,
    scoreReward: 90,
    tags: ['Club Oficial', 'Urbano', 'Juvenil'],
    linkUrl: '/clubes',
    isLive: false,
    entityParent: 'La Kalle 99.9',
    entityParentUrl: '/radio',
  },
  {
    id: 'club-006',
    type: 'club',
    name: 'Hinchas RFW',
    subtitle: 'Club Oficial · 4,300 miembros',
    imageUrl: 'https://readdy.ai/api/search-image?query=Sports%20fan%20club%20logo%20design%2C%20circular%20stadium%20and%20flag%20emblem%2C%20bold%20green%20and%20gold%20colors%2C%20passionate%20community%20brand%20identity%2C%20energetic%20fan%20culture%20design&width=160&height=160&seq=follow-hinchas-rfw&orientation=squarish',
    followers: 4300,
    weeklyGrowth: 150,
    monthlyGrowth: 650,
    trend: 'up',
    trendPercent: 5.9,
    scoreReward: 70,
    tags: ['Club Oficial', 'Deportes', 'Fútbol'],
    linkUrl: '/clubes',
    isLive: false,
    entityParent: 'Deportes Total Radio',
    entityParentUrl: '/radio',
  },

  // ═══ EVENTOS (6) ═══
  {
    id: 'evento-001',
    type: 'evento',
    name: 'Festival RFW Monterrey 2026',
    subtitle: '15 Jul 2026 · Parque Fundidora',
    imageUrl: 'https://readdy.ai/api/search-image?query=Massive%20outdoor%20music%20festival%20crowd%2C%20colorful%20stage%20lights%20and%20confetti%2C%20energetic%20concert%20atmosphere%20at%20dusk%2C%20wide%20angle%20festival%20photography%2C%20vibrant%20celebratory%20mood&width=320&height=180&seq=follow-festival-monterrey&orientation=landscape',
    followers: 8700,
    weeklyGrowth: 520,
    monthlyGrowth: 2300,
    trend: 'exploding',
    trendPercent: 15.4,
    scoreReward: 80,
    tags: ['Festival', 'Masivo', 'Música'],
    linkUrl: '/eventos',
    isLive: false,
    entityParent: 'Banda Radio MX',
    entityParentUrl: '/radio',
  },
  {
    id: 'evento-002',
    type: 'evento',
    name: 'Concierto Íntimo: Vale en Vivo',
    subtitle: '28 Jun 2026 · Teatro Vorterix',
    imageUrl: 'https://readdy.ai/api/search-image?query=Intimate%20acoustic%20concert%20venue%2C%20warm%20ambient%20lighting%20with%20candles%2C%20small%20crowd%20seated%2C%20cozy%20atmospheric%20live%20music%20setting%2C%20professional%20event%20photography%2C%20soft%20golden%20tones&width=320&height=180&seq=follow-concierto-vale&orientation=landscape',
    followers: 4200,
    weeklyGrowth: 380,
    monthlyGrowth: 1600,
    trend: 'up',
    trendPercent: 9.2,
    scoreReward: 60,
    tags: ['Concierto', 'Íntimo', 'Acústico'],
    linkUrl: '/eventos',
    isLive: false,
    entityParent: 'Radio Nueva Actitud',
    entityParentUrl: '/radio',
  },
  {
    id: 'evento-003',
    type: 'evento',
    name: 'Meet & Greet con Ricky Navarro',
    subtitle: '10 Jul 2026 · Centro Cultural',
    imageUrl: 'https://readdy.ai/api/search-image?query=Fan%20meet%20and%20greet%20event%2C%20radio%20host%20greeting%20fans%2C%20warm%20indoor%20venue%20with%20branded%20backdrop%2C%20excited%20crowd%20atmosphere%2C%20candid%20photography%2C%20genuine%20happy%20interactions&width=320&height=180&seq=follow-meet-ricky&orientation=landscape',
    followers: 3400,
    weeklyGrowth: 290,
    monthlyGrowth: 1200,
    trend: 'up',
    trendPercent: 8.5,
    scoreReward: 50,
    tags: ['Meet & Greet', 'Fan', 'Exclusivo'],
    linkUrl: '/eventos',
    isLive: false,
    entityParent: 'Banda Radio MX',
    entityParentUrl: '/radio',
  },
  {
    id: 'evento-004',
    type: 'evento',
    name: 'Sorteo Aniversario La Kalle',
    subtitle: '05 Jul 2026 · Transmisión Especial',
    imageUrl: 'https://readdy.ai/api/search-image?query=Radio%20station%20anniversary%20celebration%2C%20colorful%20studio%20with%20balloons%20and%20decorations%2C%20hosts%20celebrating%20with%20microphones%2C%20festive%20broadcast%20atmosphere%2C%20party%20photography%2C%20confetti%20and%20joy&width=320&height=180&seq=follow-sorteo-la-kalle&orientation=landscape',
    followers: 5100,
    weeklyGrowth: 610,
    monthlyGrowth: 2700,
    trend: 'exploding',
    trendPercent: 13.6,
    scoreReward: 70,
    tags: ['Sorteo', 'Aniversario', 'Premios'],
    linkUrl: '/eventos',
    isLive: false,
    entityParent: 'La Kalle 99.9',
    entityParentUrl: '/radio',
  },
  {
    id: 'evento-005',
    type: 'evento',
    name: 'Entrevista Exclusiva: Diego Montenegro',
    subtitle: '22 Jun 2026 · Rock Total Radio',
    imageUrl: 'https://readdy.ai/api/search-image?query=Professional%20radio%20studio%20interview%20setup%2C%20two%20people%20at%20microphones%2C%20warm%20intimate%20lighting%2C%20serious%20engaged%20conversation%2C%20broadcast%20journalism%20photography%2C%20documentary%20style&width=320&height=180&seq=follow-entrevista-diego&orientation=landscape',
    followers: 2100,
    weeklyGrowth: 180,
    monthlyGrowth: 800,
    trend: 'stable',
    trendPercent: 4.8,
    scoreReward: 40,
    tags: ['Entrevista', 'Exclusiva', 'Comedia'],
    linkUrl: '/eventos',
    isLive: false,
    entityParent: 'Rock Total Radio',
    entityParentUrl: '/radio',
  },
  {
    id: 'evento-006',
    type: 'evento',
    name: 'Noche de Leyendas del Deporte',
    subtitle: '18 Jul 2026 · Deportes Total Radio',
    imageUrl: 'https://readdy.ai/api/search-image?query=Sports%20legends%20panel%20event%2C%20iconic%20athletes%20on%20stage%2C%20dramatic%20spotlight%20lighting%2C%20large%20enthusiastic%20audience%2C%20sports%20hall%20of%20fame%20atmosphere%2C%20epic%20event%20photography&width=320&height=180&seq=follow-leyendas-deporte&orientation=landscape',
    followers: 3800,
    weeklyGrowth: 310,
    monthlyGrowth: 1400,
    trend: 'up',
    trendPercent: 7.4,
    scoreReward: 60,
    tags: ['Deportes', 'Leyendas', 'Panel'],
    linkUrl: '/eventos',
    isLive: false,
    entityParent: 'Deportes Total Radio',
    entityParentUrl: '/radio',
  },

  // ═══ CONCURSOS (6) ═══
  {
    id: 'concurso-001',
    type: 'concurso',
    name: 'La Voz de la Banda',
    subtitle: 'Canto Regional · Premio $10,000 MXN',
    imageUrl: 'https://readdy.ai/api/search-image?query=Singing%20competition%20stage%2C%20spotlight%20on%20microphone%20center%20stage%2C%20dramatic%20red%20and%20gold%20lighting%2C%20talent%20show%20atmosphere%2C%20professional%20performance%20photography%2C%20grand%20theatrical%20setting&width=320&height=180&seq=follow-voz-banda&orientation=landscape',
    followers: 6200,
    weeklyGrowth: 480,
    monthlyGrowth: 2100,
    trend: 'exploding',
    trendPercent: 12.3,
    scoreReward: 90,
    tags: ['Canto', 'Regional', 'Gran Premio'],
    linkUrl: '/competencias',
    isLive: false,
    entityParent: 'Banda Radio MX',
    entityParentUrl: '/radio',
  },
  {
    id: 'concurso-002',
    type: 'concurso',
    name: 'Desafío Deportivo Total',
    subtitle: 'Trivia Deportiva · Premio $5,000 USD',
    imageUrl: 'https://readdy.ai/api/search-image?query=Sports%20trivia%20competition%20event%2C%20contestant%20buzzers%20and%20screens%2C%20energetic%20game%20show%20atmosphere%2C%20dramatic%20competitive%20lighting%2C%20sports%20quiz%20show%20photography%2C%20dynamic%20action&width=320&height=180&seq=follow-desafio-deportivo&orientation=landscape',
    followers: 4800,
    weeklyGrowth: 350,
    monthlyGrowth: 1500,
    trend: 'up',
    trendPercent: 8.9,
    scoreReward: 80,
    tags: ['Trivia', 'Deportes', 'Premio'],
    linkUrl: '/competencias',
    isLive: false,
    entityParent: 'Deportes Total Radio',
    entityParentUrl: '/radio',
  },
  {
    id: 'concurso-003',
    type: 'concurso',
    name: 'Mejor Cover del Mes',
    subtitle: 'Musical · Premio Grabación Profesional',
    imageUrl: 'https://readdy.ai/api/search-image?query=Music%20cover%20competition%2C%20acoustic%20guitar%20performer%20on%20stage%2C%20warm%20spotlight%20illumination%2C%20intimate%20music%20venue%20atmosphere%2C%20artistic%20performance%20photography%2C%20creative%20musical%20expression&width=320&height=180&seq=follow-mejor-cover&orientation=landscape',
    followers: 3700,
    weeklyGrowth: 270,
    monthlyGrowth: 1200,
    trend: 'up',
    trendPercent: 7.1,
    scoreReward: 70,
    tags: ['Musical', 'Creativo', 'Grabación'],
    linkUrl: '/competencias',
    isLive: false,
    entityParent: 'Radio Nueva Actitud',
    entityParentUrl: '/radio',
  },
  {
    id: 'concurso-004',
    type: 'concurso',
    name: 'Rifa Anual RFW 2026',
    subtitle: 'Sorteo · Premio Mayor: Auto 0km',
    imageUrl: 'https://readdy.ai/api/search-image?query=Grand%20prize%20raffle%20event%2C%20luxury%20car%20on%20display%20with%20confetti%2C%20excited%20crowd%20atmosphere%2C%20celebratory%20golden%20lighting%2C%20sweepstakes%20photography%2C%20festive%20winning%20moment&width=320&height=180&seq=follow-rifa-anual&orientation=landscape',
    followers: 9200,
    weeklyGrowth: 780,
    monthlyGrowth: 3400,
    trend: 'exploding',
    trendPercent: 16.8,
    scoreReward: 100,
    tags: ['Sorteo', 'Gran Premio', 'Auto'],
    linkUrl: '/competencias',
    isLive: false,
    entityParent: 'RFW Central',
    entityParentUrl: '/radio',
  },
  {
    id: 'concurso-005',
    type: 'concurso',
    name: 'Batalla de Locutores Junio',
    subtitle: 'Duelo · Premio $2,000 USD + Insignia',
    imageUrl: 'https://readdy.ai/api/search-image?query=Radio%20host%20battle%20competition%2C%20two%20hosts%20facing%20off%20at%20microphones%2C%20dramatic%20split%20lighting%2C%20intense%20competitive%20atmosphere%2C%20broadcast%20duel%20photography%2C%20dynamic%20energy&width=320&height=180&seq=follow-batalla-locutores&orientation=landscape',
    followers: 3100,
    weeklyGrowth: 240,
    monthlyGrowth: 1000,
    trend: 'stable',
    trendPercent: 5.6,
    scoreReward: 60,
    tags: ['Duelo', 'Locutores', 'Insignia'],
    linkUrl: '/competencias',
    isLive: false,
    entityParent: 'La Mexicana FM',
    entityParentUrl: '/radio',
  },
  {
    id: 'concurso-006',
    type: 'concurso',
    name: 'Diseñá el Logo del Club',
    subtitle: 'Creativo · Premio Merch Oficial + $500',
    imageUrl: 'https://readdy.ai/api/search-image?query=Design%20competition%20creative%20workspace%2C%20digital%20tablet%20with%20logo%20sketches%2C%20colorful%20artistic%20atmosphere%2C%20modern%20studio%20setting%2C%20creative%20contest%20photography%2C%20inspirational%20artistic%20vibe&width=320&height=180&seq=follow-diseña-logo&orientation=landscape',
    followers: 2400,
    weeklyGrowth: 310,
    monthlyGrowth: 1300,
    trend: 'exploding',
    trendPercent: 14.9,
    scoreReward: 50,
    tags: ['Creativo', 'Diseño', 'Club'],
    linkUrl: '/competencias',
    isLive: false,
    entityParent: 'La Kalle 99.9',
    entityParentUrl: '/radio',
  },
];

// ─── HELPERS ──────────────────────────────────────────────

export function getAllFollowables(): FollowableEntity[] {
  return followableEntities;
}

export function getFollowablesByType(type: FollowEntityType | 'todos'): FollowableEntity[] {
  if (type === 'todos') return followableEntities;
  return followableEntities.filter((e) => e.type === type);
}

export function getMostFollowed(limit = 10): FollowableEntity[] {
  return [...followableEntities].sort((a, b) => b.followers - a.followers).slice(0, limit);
}

export function getTrendingFollowers(limit = 10): FollowableEntity[] {
  return [...followableEntities].sort((a, b) => b.trendPercent - a.trendPercent).slice(0, limit);
}

export function getMostFollowedByType(type: FollowEntityType, limit = 5): FollowableEntity[] {
  return [...followableEntities].filter((e) => e.type === type).sort((a, b) => b.followers - a.followers).slice(0, limit);
}

export function getFollowableById(id: string): FollowableEntity | undefined {
  return followableEntities.find((e) => e.id === id);
}

export function getLiveNow(): FollowableEntity[] {
  return followableEntities.filter((e) => e.isLive);
}

export function getListenerFollowedEntities(profile: ListenerFollowedProfile): FollowableEntity[] {
  const allIds = [
    ...profile.followedRadios,
    ...profile.followedProgramas,
    ...profile.followedLocutores,
    ...profile.followedClubes,
  ];
  return followableEntities.filter((e) => allIds.includes(e.id));
}

export function formatFollowerCount(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

export function formatGrowthPercent(num: number): string {
  return `${num >= 0 ? '+' : ''}${num.toFixed(1)}%`;
}